import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { ShellComponent } from './shell/shell.component';

export const routes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./features/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./features/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
      }
    ]
  },
  {
    path: 'guide/:token',
    loadComponent: () => import('./features/guest-guide/guest-guide.component').then(m => m.GuestGuideComponent)
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      {
        path: 'portfolio',
        loadComponent: () => import('./features/portfolio/portfolio.component').then(m => m.PortfolioComponent)
      },
      {
        path: 'portfolio/add',
        loadComponent: () => import('./features/portfolio/add-property/add-property.component').then(m => m.AddPropertyComponent)
      },
      {
        path: 'portfolio/:id',
        loadComponent: () => import('./features/portfolio/property-detail/property-detail.component').then(m => m.PropertyDetailComponent)
      },
      {
        path: 'tasks',
        loadComponent: () => import('./features/tasks/tasks.component').then(m => m.TasksComponent)
      },
      {
        path: 'tasks/:id',
        loadComponent: () => import('./features/tasks/task-detail/task-detail.component').then(m => m.TaskDetailComponent)
      },
      {
        path: 'work-orders',
        loadComponent: () => import('./features/work-orders/work-orders.component').then(m => m.WorkOrdersComponent)
      },
      {
        path: 'inspections',
        loadComponent: () => import('./features/inspections/inspections.component').then(m => m.InspectionsComponent)
      },
      {
        path: 'workforce',
        canActivate: [roleGuard('Owner', 'Manager', 'Admin', 'CompanyAdmin', 'SuperAdmin')],
        loadComponent: () => import('./features/workforce/workforce.component').then(m => m.WorkforceComponent)
      },
      {
        path: 'workforce/:id',
        canActivate: [roleGuard('Owner', 'Manager', 'Admin', 'CompanyAdmin', 'SuperAdmin')],
        loadComponent: () => import('./features/workforce/staff-profile/staff-profile.component').then(m => m.StaffProfileComponent)
      },
      {
        path: 'reports',
        canActivate: [roleGuard('Owner', 'Manager', 'Admin', 'CompanyAdmin', 'SuperAdmin')],
        loadComponent: () => import('./features/reports/reports.component').then(m => m.ReportsComponent)
      },
      {
        path: 'payments',
        canActivate: [roleGuard('Owner', 'Manager', 'Admin', 'CompanyAdmin', 'SuperAdmin')],
        loadComponent: () => import('./features/payments/payments.component').then(m => m.PaymentsComponent)
      },
      {
        path: 'schedule',
        loadComponent: () => import('./features/schedule/schedule.component').then(m => m.ScheduleComponent)
      },
      {
        path: 'reservations',
        loadComponent: () => import('./features/reservations/reservations.component').then(m => m.ReservationsComponent)
      },
      {
        path: 'reservations/:id',
        loadComponent: () => import('./features/reservations/reservation-detail/reservation-detail.component').then(m => m.ReservationDetailComponent)
      },
      {
        path: 'insights',
        canActivate: [roleGuard('Owner', 'Manager', 'Admin', 'CompanyAdmin', 'SuperAdmin')],
        loadComponent: () => import('./features/insights/insights.component').then(m => m.InsightsComponent)
      },
      {
        path: 'tenants',
        canActivate: [roleGuard('SuperAdmin')],
        loadComponent: () => import('./features/tenants/tenants.component').then(m => m.TenantsComponent)
      },
      {
        path: 'inspections/:id',
        loadComponent: () => import('./features/inspections/inspection-detail/inspection-detail.component').then(m => m.InspectionDetailComponent)
      },
      {
        path: 'worklists',
        loadComponent: () => import('./features/worklists/worklists.component').then(m => m.WorklistsComponent)
      },
      {
        path: 'worklists/new',
        loadComponent: () => import('./features/worklists/worklist-form/worklist-form.component').then(m => m.WorklistFormComponent)
      },
      {
        path: 'worklists/group/:groupId',
        loadComponent: () => import('./features/worklists/list-group-detail/list-group-detail.component').then(m => m.ListGroupDetailComponent)
      },
      {
        path: 'worklists/group/:groupId/new',
        loadComponent: () => import('./features/worklists/worklist-form/worklist-form.component').then(m => m.WorklistFormComponent)
      },
      {
        path: 'worklists/:id/edit',
        loadComponent: () => import('./features/worklists/worklist-form/worklist-form.component').then(m => m.WorklistFormComponent)
      },
      {
        path: 'worklists/:id',
        loadComponent: () => import('./features/worklists/worklist-detail/worklist-detail.component').then(m => m.WorklistDetailComponent)
      },
      {
        path: 'messages',
        loadComponent: () => import('./features/messaging/messaging.component').then(m => m.MessagingComponent)
      },
      {
        path: 'profile',
        loadComponent: () => import('./features/profile/profile.component').then(m => m.ProfileComponent)
      },
      {
        path: 'help',
        loadComponent: () => import('./features/help/help.component').then(m => m.HelpComponent)
      },
      {
        path: 'settings',
        canActivate: [roleGuard('Owner', 'Manager', 'Admin', 'CompanyAdmin', 'SuperAdmin')],
        loadComponent: () => import('./features/settings/settings.component').then(m => m.SettingsComponent),
        children: [
          { path: '', redirectTo: 'general', pathMatch: 'full' },
          { path: 'general',          loadComponent: () => import('./features/settings/sections/general.component').then(m => m.GeneralSettingsComponent) },
          { path: 'availability',     loadComponent: () => import('./features/settings/sections/availability.component').then(m => m.AvailabilityComponent) },
          { path: 'scheduling',       loadComponent: () => import('./features/settings/sections/scheduling.component').then(m => m.SchedulingSettingsComponent) },
          { path: 'messaging',        loadComponent: () => import('./features/settings/sections/messaging.component').then(m => m.MessagingSettingsComponent) },
          { path: 'id-verification',  loadComponent: () => import('./features/settings/sections/id-verification.component').then(m => m.IdVerificationComponent) },
          { path: 'permissions',      loadComponent: () => import('./features/settings/sections/permissions.component').then(m => m.PermissionsComponent) },
          { path: 'task-templates',   loadComponent: () => import('./features/settings/sections/task-templates.component').then(m => m.TaskTemplatesComponent) },
          { path: 'task-tags',        loadComponent: () => import('./features/settings/sections/task-tags.component').then(m => m.TaskTagsComponent) },
          { path: 'task-departments', loadComponent: () => import('./features/settings/sections/task-departments.component').then(m => m.TaskDepartmentsComponent) },
          { path: 'property-groups',  loadComponent: () => import('./features/settings/sections/property-groups.component').then(m => m.PropertyGroupsComponent) },
          { path: 'property-tags',    loadComponent: () => import('./features/settings/sections/property-tags.component').then(m => m.PropertyTagsComponent) },
          { path: 'supplies',         loadComponent: () => import('./features/settings/sections/supplies.component').then(m => m.SuppliesComponent) },
          { path: 'roles',            loadComponent: () => import('./features/settings/sections/roles.component').then(m => m.RolesComponent) },
        ]
      },
    ]
  },
  { path: '**', redirectTo: 'dashboard' }
];
